<template>
  <div
    v-if="resp.type === 'answer' && (resp.show || editable)"
    :class="{edit: editable, 'dynamic': dynamic, 'notable': notable}"
    class="response"
  >
    <template v-if="editable && !dynamic">
      <Checkbox
        v-show="editable"
        v-model="checked"
        class="response-check"
        @click.native="checkboxClick(index, group, $event)"
      />
    </template>
    <IndividualResponse :response="resp.content" :keywords="keywords" />
  </div>
  <div v-else-if="resp.type === 'group' && (showChildren || editable)" class="responses-group">
    <div :class="sentimentClasses" class="response-group-title">
      {{ resp.content }}
      <button
        v-if="editable"
        class="toggle-responses-button"
        @click="toggleAllCheckboxes(resp)"
      >
        Select {{ allChecked ? 'None' : 'All' }}
      </button>
    </div>
    <template v-if="!lazy || load">
      <ResponsesTemplate
        v-for="(child, idx) in resp.children"
        :key="idx"
        :resp="child"
        :editable="editable"
        :keywords="keywords"
        :index="idx"
        :group="resp.children"
        :toggle-all="allChecked"
        :nested-checkbox-click="checkboxClick"
        :active-chart="activeChart"
        :sentiment-analysis-mode="sentimentAnalysisMode"
        @toggleAllCheckboxes="$emit('toggleAllCheckboxes', $event)"
      />
    </template>
    <template v-else>
      <ResponsesTemplate
        v-for="(child, idx) in resp.children.slice(0, 10)"
        :key="idx"
        :resp="child"
        :editable="editable"
        :keywords="keywords"
        :index="idx"
        :group="resp.children"
        :toggle-all="allChecked"
        :nested-checkbox-click="checkboxClick"
        :active-chart="activeChart"
        :sentiment-analysis-mode="sentimentAnalysisMode"
        @toggleAllCheckboxes="$emit('toggleAllCheckboxes', $event)"
      />
    </template>
  </div>
</template>

<script>
import { forEach } from 'lodash-es'
import IndividualResponse from './IndividualResponse.vue'
import { Checkbox } from '@alpha/ink'
import { getResponseIDs } from '@/utilities/text-response-helpers.js'

export default {
  name: 'ResponsesTemplate',
  components: {
    IndividualResponse,
    Checkbox
  },
  props: {
    resp: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    group: {
      type: Array,
      required: true
    },
    toggleAll: {
      type: Boolean,
      required: true
    },
    lazy: {
      type: Boolean,
      default: false
    },
    keywords: {
      type: [Array, Boolean],
      default: false
    },
    dynamic: {
      type: Boolean,
      default: false
    },
    notable: {
      type: Boolean,
      default: false
    },
    load: {
      type: Boolean,
      default: false
    },
    nestedCheckboxClick: {
      type: [Function, Boolean],
      default: false
    },
    activeChart: {
      type: String,
      default: 'original'
    },
    sentimentAnalysisMode: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      allChecked: true
    }
  },
  computed: {
    showChildren () {
      var showing = false
      forEach(this.resp.children, function (child) {
        if (child.show) showing = true
      })
      return showing
    },
    sentimentClasses () {
      if (!this.sentimentAnalysisMode || !this.resp.content) return ''
      const sentiments = {
        Positive: 'positive',
        Neutral: 'neutral',
        Negative: 'negative'
      }
      return sentiments[this.resp.content] || ''
    },
    checked: {
      get: function () {
        return this.resp.show
      },
      set: function () {

      }
    }
  },
  watch: {
    toggleAll () {
      // each group needs to maintain its own state of whether the group is checked or not...
      this.allChecked = this.toggleAll
    }
  },
  methods: {
    updateResponseContent (val) {
      this.$set(this.resp, 'content', val)
    },
    checkboxClick (index, group, $event) {
      // In a nested response template the prop 'nestedCheckboxClick' will exist and point to the root level component's version of this function.
      if (this.nestedCheckboxClick) {
        this.nestedCheckboxClick(index, group, $event)
      } else {
        this.$emit('checkboxClick', {
          event: $event,
          index,
          group,
          slice: this.activeChart
        })
      }
    },
    toggleAllCheckboxes (resp) {
      this.allChecked = !this.allChecked
      this.$emit('toggleAllCheckboxes', { toggleAll: this.allChecked, rids: getResponseIDs(resp), slice: this.activeChart })
    }
  }
}
</script>

<style scoped lang="scss">
.positive {
  background-color: #d9ecdd !important;
  color: #3e3e3e;
}

.neutral {
  background-color: #fcf3d4 !important;
  color: #3e3e3e;
}

.negative {
  background-color: #fcccca !important;
  color: #3e3e3e;
}

/* Text responses */

.responses-group .responses-group .response-group-title {
  background: #f1f1f1;
  font-size: 16px;
  padding: 6px 14px;
}

.response.notable {
  background: hsla(198, 50%, 90%, 1);
}

.response.notable:nth-child(2n-1) {
  background: hsla(198, 50%, 90%, 0.6);
}

.response.dynamic {
  padding: 10px 20px;
}

.response.edit {
  justify-content: flex-start;
  align-items: center;
}

input[type="checkbox"] {
  margin-right: 10px;
  margin-top: 0;
  flex-shrink: 0;
}

input[type="checkbox"].hide {
  display: none;
}

.response {
  background: #fff;
  font-size: 14px;
  line-height: 1.4;
  padding: 4px 20px;
  display: flex;
  color: $grey-dark;
}

.response:nth-child(2n) {
  background: #fafafa;
}

.response-group-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #eaeaea;
  font-size: 16px;
  padding: 6px 14px;
  font-weight: bold;
}

.toggle-responses-button {
  color: $primary;
  background: transparent;
  padding: 0;
  border: 0;
  font-size: 12px;
  margin-top: 1px;
}

.response-check {
  max-width: 2rem;
}

/* Some IE hacks */
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .response-check {
    max-width: 30px;
    width: 30px;
    flex-shrink: 0;
  }
}
</style>
